import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Scale, ArrowLeft, User, Building2, Landmark } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

const ACCOUNT_TYPES = [
  { value: "individual", label: "Individual", icon: User, desc: "Personal legal & court forms" },
  { value: "company", label: "Company", icon: Building2, desc: "Business & SOS filings" },
  { value: "attorney_firm", label: "Attorney Firm", icon: Landmark, desc: "Law firm & client matters" },
];

const ONBOARDING_ROUTE = { individual: "/forms", company: "/onboarding/business", attorney_firm: "/onboarding/business" };

export default function Login() {
  const { login, register, loginWithGoogle } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const from = useLocation().state?.from;
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [accountType, setAccountType] = useState("individual");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !agreed) {
      toast.error(t("login.acceptTerms"));
      return;
    }
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success(t("login.welcomeToast"));
        navigate(from || "/dashboard");
      } else {
        const u = await register(form.name, form.email, form.password, form.company, agreed, undefined, accountType);
        toast.success(t("login.welcomeToast"));
        navigate(ONBOARDING_ROUTE[u?.account_type] || "/dashboard");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("login.authFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/8112110/pexels-photo-8112110.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <Link to="/" className="relative flex items-center gap-2 w-fit">
          <span className="grid place-items-center w-9 h-9 bg-primary-foreground text-primary"><Scale size={18} /></span>
          <span className="font-serif text-xl font-semibold">NC Legal Forms</span>
        </Link>
        <div className="relative">
          <p className="overline text-accent mb-4">{t("login.brandOverline")}</p>
          <h1 className="font-serif text-5xl leading-[1.05] tracking-tight mb-6">
            {t("login.brandTitle")}
          </h1>
          <p className="text-primary-foreground/70 max-w-md">
            {t("login.brandDesc")}
          </p>
        </div>
        <div className="relative text-xs text-primary-foreground/50">{t("login.brandTrust")}</div>
      </div>

      {/* Form panel */}
      <div className="flex flex-col justify-center px-6 sm:px-16 py-12">
        <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-10 w-fit">
          <ArrowLeft size={16} /> {t("login.backHome")}
        </Link>
        <div className="max-w-sm w-full">
          <h2 className="font-serif text-4xl tracking-tight mb-2">{mode === "login" ? t("login.welcomeBack") : t("login.createAccount")}</h2>
          <p className="text-muted-foreground mb-8 text-sm">
            {mode === "login" ? t("login.loginSub") : t("login.registerSub")}
          </p>

          <Button onClick={loginWithGoogle} variant="outline" data-testid="google-login-btn"
            className="w-full rounded-sm h-11 gap-3 mb-6 hover:bg-secondary">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            {t("login.continueGoogle")}
          </Button>

          {mode === "register" && (
            <p className="text-[0.7rem] text-muted-foreground -mt-4 mb-6 text-center">
              {t("login.googleAgreePre")}
              <Link to="/legal/terms" target="_blank" className="text-accent hover:underline">{t("login.terms")}</Link>,{" "}
              <Link to="/legal/privacy" target="_blank" className="text-accent hover:underline">{t("login.privacy")}</Link> &amp;{" "}
              <Link to="/legal/eula" target="_blank" className="text-accent hover:underline">{t("login.eula")}</Link>.
            </p>
          )}

          <div className="flex items-center gap-4 mb-6">
            <span className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">{t("login.or")}</span><span className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div data-testid="account-type-selector">
                  <Label className="text-xs">I'm signing up as</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1.5">
                    {ACCOUNT_TYPES.map((a) => {
                      const activeType = accountType === a.value;
                      return (
                        <button type="button" key={a.value} data-testid={`account-type-${a.value}`}
                          onClick={() => setAccountType(a.value)}
                          className={`flex flex-col items-center text-center gap-1.5 p-3 border-2 transition-colors ${activeType ? "border-accent bg-accent/10 text-foreground" : "border-black/15 hover:border-black/40 text-muted-foreground"}`}>
                          <a.icon size={20} className={activeType ? "text-accent" : ""} />
                          <span className="text-xs font-semibold leading-tight">{a.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[0.7rem] text-muted-foreground mt-1.5">{ACCOUNT_TYPES.find((a) => a.value === accountType)?.desc}</p>
                </div>
                <div>
                  <Label htmlFor="name" className="text-xs">{t("login.fullName")}</Label>
                  <Input id="name" data-testid="register-name-input" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-11 mt-1" placeholder={t("login.namePlaceholder")} />
                </div>
                {accountType !== "individual" && (
                <div>
                  <Label htmlFor="company" className="text-xs">{accountType === "attorney_firm" ? "Firm name" : "Company name"} <span className="text-muted-foreground">{t("login.optional")}</span></Label>
                  <Input id="company" data-testid="register-company-input" value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-sm h-11 mt-1" placeholder={accountType === "attorney_firm" ? "Acme Law, PLLC" : "Acme Holdings LLC"} />
                </div>
                )}
              </>
            )}
            <div>
              <Label htmlFor="email" className="text-xs">{t("login.email")}</Label>
              <Input id="email" type="email" data-testid="login-email-input" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm h-11 mt-1" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs">{t("login.password")}</Label>
              <Input id="password" type="password" data-testid="login-password-input" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm h-11 mt-1" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit-btn"
              className="w-full rounded-sm h-11 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
              {busy ? t("login.pleaseWait") : mode === "login" ? t("login.logIn") : t("login.createBtn")}
            </Button>

            {mode === "register" && (
              <label className="flex items-start gap-3 text-xs text-muted-foreground cursor-pointer">
                <Checkbox data-testid="agree-terms-checkbox" checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" />
                <span>
                  {t("login.agreePre")}
                  <Link to="/legal/terms" target="_blank" className="text-accent hover:underline">{t("login.termsFull")}</Link>,{" "}
                  <Link to="/legal/privacy" target="_blank" className="text-accent hover:underline">{t("login.privacyFull")}</Link>, {t("login.andConn")}
                  <Link to="/legal/eula" target="_blank" className="text-accent hover:underline">{t("login.eulaFull")}</Link>.
                </span>
              </label>
            )}
          </form>

          <p className="text-sm text-muted-foreground mt-6">
            {mode === "login" ? t("login.newHere") : t("login.haveAccount")}
            <button data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-accent font-medium hover:underline">
              {mode === "login" ? t("login.createLink") : t("login.loginLink")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
